import React, { useRef, useState } from "react";
import { Paperclip, Download, Upload, Cpu, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { uploadAttachment, attachmentDownloadUrl, processAttachment } from "../../api";

const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export const AttachmentsBlock = ({ request, canUpload, canProcess, onChanged }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const attachments = request.attachments || [];

  const runProcess = async (a) => {
    setProcessingId(a.id);
    try {
      const res = await processAttachment(a.id);
      if (res.status === "ok") {
        toast.success(`Processed into ${res.entity_name}`, {
          description: `${res.rows_ingested ?? res.summary?.total_items ?? ""} lines now in the engagement workspace.`,
        });
      } else {
        toast.error("Engine rejected the statement", { description: res.reason || res.status });
      }
      onChanged();
    } catch (err) {
      toast.error("Processing failed", {
        description:
          (err.response && err.response.data && typeof err.response.data.detail === "string" && err.response.data.detail) ||
          "Please try again.",
      });
    }
    setProcessingId(null);
  };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is over the 10 MB limit`);
        continue;
      }
      try {
        await uploadAttachment(request.id, file);
        toast.success(`${file.name} attached`);
      } catch (err) {
        toast.error(`Upload failed: ${file.name}`, {
          description:
            (err.response && err.response.data && typeof err.response.data.detail === "string" && err.response.data.detail) ||
            "Please try again.",
        });
      }
    }
    setBusy(false);
    onChanged();
  };

  if (!canUpload && attachments.length === 0) return null;

  return (
    <div className="pt-2 border-t border-white/5 space-y-2" data-testid={`attachments-${request.id}`}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 flex items-center space-x-1">
          <Paperclip className="w-3 h-3" />
          <span>Documents ({attachments.length})</span>
        </span>
        {canUpload && (
          <>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept=".pdf,.csv,.png,.jpg,.jpeg,.xlsx,.xls,.txt,.docx"
              className="hidden"
              onChange={onFiles}
              data-testid={`attach-input-${request.id}`}
            />
            <button
              data-testid={`attach-btn-${request.id}`}
              disabled={busy}
              onClick={() => inputRef.current && inputRef.current.click()}
              className="px-2.5 py-1 bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-300 text-[10px] font-semibold rounded-md transition flex items-center space-x-1 disabled:opacity-50"
            >
              <Upload className="w-3 h-3" />
              <span>{busy ? "Uploading…" : "Attach Files"}</span>
            </button>
          </>
        )}
      </div>
      {attachments.length > 0 && (
        <div className="space-y-1">
          {attachments.map((a) => (
            <div key={a.id} className="flex items-center justify-between bg-white/[0.03] border border-white/5 rounded-md px-2.5 py-1.5" data-testid={`attachment-row-${a.id}`}>
              <div className="flex items-center space-x-2 min-w-0">
                <Paperclip className="w-3 h-3 text-blue-400 flex-shrink-0" />
                <span className="text-[11px] text-zinc-300 truncate">{a.original_filename}</span>
                <span className="text-[10px] text-zinc-600 font-mono flex-shrink-0">{fmtSize(a.size)}</span>
              </div>
              <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                {canProcess && a.original_filename.toLowerCase().endsWith(".csv") && (
                  a.processed_into ? (
                    <span className="text-[10px] text-emerald-400 font-semibold flex items-center space-x-1" data-testid={`attachment-processed-${a.id}`}>
                      <CheckCircle2 className="w-3 h-3" />
                      <span>Processed</span>
                    </span>
                  ) : (
                    <button
                      data-testid={`attachment-process-${a.id}`}
                      disabled={processingId === a.id}
                      onClick={() => runProcess(a)}
                      className="px-2 py-1 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-[10px] font-bold rounded-md transition-colors flex items-center space-x-1"
                    >
                      <Cpu className="w-3 h-3" />
                      <span>{processingId === a.id ? "Processing…" : "Process in Engine"}</span>
                    </button>
                  )
                )}
                <a
                  data-testid={`attachment-download-${a.id}`}
                  href={attachmentDownloadUrl(a.id)}
                  className="text-zinc-400 hover:text-white transition"
                  title={`Download ${a.original_filename}`}
                >
                  <Download className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
      {canUpload && attachments.length === 0 && (
        <p className="text-[10px] text-zinc-600">Attach bank statements or documents (PDF, CSV, images, Excel — max 10 MB each).</p>
      )}
    </div>
  );
};
